import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";

// Mock data for preview
const mockData = {
	value: 42,
	maxValue: 52,
	unit: "Books Read",
	text: "days",
	icon: "flame",
};

// Approximate widths for preview based on typical iPhone screen
const FULL_WIDTH = 340; // Full width minus some padding
const HALF_WIDTH = FULL_WIDTH / 2 - 8; // Half width minus gap

const ComponentPreview = ({ config }) => {
	const { header, size = "half", dataType = "value", dataConfig = {} } = config; // Default to half

	const renderData = () => {
		switch (dataType) {
			case "progressbar":
				const maxValue = dataConfig.dynamicMaxValue ? mockData.maxValue : dataConfig.maxValue;
				const progress = maxValue ? (mockData.value / maxValue) * 100 : 0;
				return (
					<View style={styles.dataContainer}>
						<View style={styles.progressHeader}>
							<Text style={styles.progressText}>
								{mockData.value} of {maxValue || "?"} {dataConfig.unit || mockData.unit}
							</Text>
							{dataConfig.showPercentage && <Text style={styles.percentageText}>{Math.round(progress)}%</Text>}
						</View>
						<View style={styles.progressBarBackground}>
							<View style={[styles.progressBarFill, { width: `${progress}%` }]} />
						</View>
					</View>
				);
			case "value":
				return (
					<View style={styles.dataContainer}>
						<Text style={styles.valueText}>
							{dataConfig.prefix || ""}
							{mockData.value}
							{dataConfig.suffix || ""}
						</Text>
						<Text style={styles.unitText}>{dataConfig.unit || mockData.unit}</Text>
					</View>
				);
			case "icon":
				return (
					<View style={[styles.dataContainer, styles.iconDataContainer]}>
						{dataConfig.iconName && <FontAwesome6 name={dataConfig.iconName} size={24} color="#1C1C1E" />}
						<Text style={styles.iconText}>
							{mockData.value} {dataConfig.text || mockData.text}
						</Text>
					</View>
				);
			// Add 'datasource' case later if needed
			default:
				return <Text style={styles.unitText}>Select Data Type</Text>;
		}
	};

	// Map size to style
	const sizeStyle = size === "full" ? styles.fullWidth : styles.halfWidth;

	return (
		<View style={[styles.previewContainer, sizeStyle]}>
			{header && <Text style={styles.headerText}>{header}</Text>}
			{renderData()}
		</View>
	);
};

const styles = StyleSheet.create({
	previewContainer: {
		backgroundColor: "#FFFFFF", // Light Mode Background
		borderRadius: 12,
		padding: 16,
		margin: 10, // Keep margin for visual separation in editor
		alignItems: "flex-start",
		shadowColor: "#000", // Optional: Add subtle shadow for depth
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.1,
		shadowRadius: 3,
		elevation: 2,
	},
	// --- Size Variants ---
	halfWidth: {
		width: HALF_WIDTH,
		padding: 12, // Slightly less padding for smaller size
	},
	fullWidth: {
		width: FULL_WIDTH,
		padding: 16,
	},
	// --- Header ---
	headerText: {
		color: "#333333", // Darker Header
		fontSize: 16,
		fontWeight: "600",
		marginBottom: 12,
	},
	// --- Data Containers ---
	dataContainer: {
		width: "100%",
	},
	iconDataContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	// --- Progress Bar ---
	progressHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "baseline",
		marginBottom: 8,
		width: "100%",
	},
	progressText: {
		color: "#666666", // Darker Text
		fontSize: 12,
	},
	progressBarBackground: {
		height: 8,
		backgroundColor: "#E0E0E0", // Light background for progress bar
		borderRadius: 4,
		overflow: "hidden",
		width: "100%",
	},
	progressBarFill: {
		height: "100%",
		backgroundColor: "#007AFF", // Standard blue
		borderRadius: 4,
	},
	percentageText: {
		color: "#007AFF", // Standard blue
		fontSize: 12,
		fontWeight: "500",
	},
	// --- Value ---
	valueText: {
		color: "#1C1C1E", // Very Dark Text
		fontSize: 28,
		fontWeight: "bold",
	},
	unitText: {
		color: "#8A8A8E", // Medium Gray for units
		fontSize: 13,
		marginTop: 2,
	},
	// --- Icon + Text ---
	iconText: {
		color: "#1C1C1E", // Very Dark Text
		fontSize: 18,
		fontWeight: "500",
	},
});

export default ComponentPreview;
